import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import translationKeys from '@translationKeys'

import PartneringOneIcon from '@assets/images/house_icon.svg'
import IconButton from '@components/formControls/IconButton'
import SearchIcon from '@components/icons/SearchIcon'
import ShortlistIcon from '@components/icons/ShortlistIcon'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import updatesApi from '@modules/updates/api'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { PartneringOneWrapper, StyledImage, StyledNavLink, StyledSideNavBar, UnreadUpdatesCount } from './index.styles'

const SideNavBar = () => {
  const { user } = useAuthContext()
  const { t } = useTranslation()
  const { hasUnlimitiedAccessToProfiles: hasAccessToSearch, hasAccessToAlerts } = useFeatureFlags()

  const location = useLocation()

  const { data: updatesUnreadCount } = useQuery({
    queryKey: [updatesApi.getUpdatesUnreadCount.key],
    queryFn: () => updatesApi.getUpdatesUnreadCount.fn(),
    enabled: !!user?.id,
    placeholderData: keepPreviousData,
  })

  const unreadUpdatesCount = useMemo(() => {
    if (!updatesUnreadCount?.unreadCount) {
      return ''
    }

    if (updatesUnreadCount?.unreadCount > 99) {
      return '99+'
    }

    return `${updatesUnreadCount?.unreadCount}`
  }, [updatesUnreadCount])

  return (
    <StyledSideNavBar data-testid="side-navigation">
      <IconButton
        disabled={!hasAccessToSearch || location.pathname === '/plus/search'}
        sx={{ padding: 0, height: '60px', opacity: hasAccessToSearch ? 1 : 0.2 }}
        disableRipple
        tooltipTitle={t(translationKeys.SEARCH_LABEL) || ''}
        id="navigation-search"
      >
        <StyledNavLink to="/plus/search">
          <SearchIcon fontSize="inherit" data-testid="search-icon" />
        </StyledNavLink>
      </IconButton>

      <IconButton
        disabled={!hasAccessToSearch}
        sx={{ padding: 0, height: '60px', opacity: hasAccessToSearch ? 1 : 0.2 }}
        disableRipple
        tooltipTitle={t(translationKeys.CUSTOM_LIST_BTN) || ''}
        id="navigation-shortlist"
      >
        <StyledNavLink to="/plus/custom-list">
          <ShortlistIcon fontSize="inherit" />
        </StyledNavLink>
      </IconButton>

      {hasAccessToAlerts ? (
        <IconButton
          disabled={!hasAccessToSearch}
          sx={{ padding: 0, height: '60px', opacity: hasAccessToSearch ? 1 : 0.2, position: 'relative' }}
          disableRipple
          tooltipTitle={t(translationKeys.UPDATES) || ''}
          id="navigation-alerts"
        >
          <StyledNavLink to="/plus/alerts/updates">
            <NotificationsNoneOutlinedIcon fontSize="inherit" />
          </StyledNavLink>
          {unreadUpdatesCount ? <UnreadUpdatesCount>{unreadUpdatesCount}</UnreadUpdatesCount> : null}
        </IconButton>
      ) : null}

      <PartneringOneWrapper>
        <IconButton
          sx={{ height: '60px' }}
          disableRipple
          tooltipTitle={t(translationKeys.PARTNERING_ONE_TEXT) || ''}
          id="navigation-hub"
        >
          <StyledNavLink to="/plus/hub">
            <StyledImage
              src={PartneringOneIcon}
              alt={t(translationKeys.PARTNERING_ONE_TEXT) || ''}
              data-testid="RepositoryIcon"
            />
          </StyledNavLink>
        </IconButton>
      </PartneringOneWrapper>
    </StyledSideNavBar>
  )
}

export default SideNavBar
